import React, { useState, useEffect, useRef, useCallback } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';

import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import { themeColors } from '../../styles/theme';

// Shared editor typography / spacing — must be identical on display layer and textarea
const EDITOR = {
  fontFamily: 'Menlo, Monaco, "SFMono-Regular", Consolas, "Liberation Mono", "Courier New", monospace',
  fontSize: '13.5px',
  lineHeight: '1.65',
  padding: '20px 24px',
};

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Build the HTML to render in the display layer.
 * {{VariableName}} tokens are wrapped in a <mark> that changes only background/color
 * (no padding/margin) so cursor positions in the transparent textarea above stay aligned.
 */
function buildDisplayHtml(text, placeholder) {
  if (!text) {
    return `<span style="color:${themeColors.neutral[300]}">${escapeHtml(placeholder)}</span>`;
  }
  const highlighted = escapeHtml(text).replace(
    /\{\{([^}]*)\}\}/g,
    (match) =>
      `<mark style="background:rgba(59,77,214,0.11);color:#2231A8;border-radius:3px;padding:0;font-weight:600;font-style:normal">${match}</mark>`,
  );
  // Trailing newline ensures an empty last line renders at the correct height
  return highlighted + '\n';
}

/**
 * TextEditorModal — unified, modern text editor for prompts, templates, and any future text nodes.
 *
 * Architecture:
 *   The editor uses the "transparent textarea over styled display layer" technique:
 *   - A read-only <div> (display layer) renders HTML with {{var}} tokens highlighted.
 *   - A transparent <textarea> (edit layer) sits on top and handles all keyboard input.
 *   - Both share identical font / padding / line-height so text aligns perfectly.
 *   - Scroll position is synced from the textarea scroll event to the display layer.
 */
export default function TextEditorModal({
  open,
  onClose,
  title = 'Editor',
  nodeLabel,
  nodeColor,     // { softBg, text, border } from NODE_COLORS
  nodeIcon,      // React element
  value: externalValue = '',
  onSave,
  availableVariables = [],
  placeholder = 'Start writing...',
}) {
  const [value, setValue] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const textareaRef = useRef(null);
  const displayRef = useRef(null);
  const valueRef = useRef(value);
  useEffect(() => { valueRef.current = value; }, [value]);

  // Re-initialise from external value each time the modal opens; reset fullscreen on close
  useEffect(() => {
    if (open) {
      setValue(externalValue);
    } else {
      setIsFullscreen(false);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync display layer scroll to match the textarea
  const syncScroll = () => {
    if (displayRef.current && textareaRef.current) {
      displayRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  };

  // Save without closing — used by Cmd+S (works in both windowed and fullscreen)
  const quickSave = useCallback(() => {
    onSave?.(valueRef.current);
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  }, [onSave]);

  // Save and close — used by the Save button
  const handleSave = useCallback(() => {
    onSave?.(value);
    onClose();
  }, [value, onSave, onClose]);

  // Ctrl+S / Cmd+S — save in place, never close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        quickSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, quickSave]);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const insertVariable = (variable) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const token = `{{${variable}}}`;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    setValue((prev) => prev.substring(0, start) + token + prev.substring(end));
    setTimeout(() => {
      textarea.focus();
      const pos = start + token.length;
      textarea.selectionStart = pos;
      textarea.selectionEnd = pos;
    }, 10);
  };

  const charCount = value.length;
  const lineCount = (value.match(/\n/g) || []).length + 1;
  const displayHtml = buildDisplayHtml(value, placeholder);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          width: isFullscreen ? '100vw' : 'min(920px, 88vw)',
          height: isFullscreen ? '100vh' : 'min(720px, 86vh)',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden',
          borderRadius: isFullscreen ? 0 : '14px',
          border: isFullscreen ? 'none' : `1px solid ${themeColors.neutral[200]}`,
          boxShadow: isFullscreen ? 'none' : '0 32px 64px -12px rgba(0,0,0,0.2), 0 12px 28px -6px rgba(0,0,0,0.08)',
          transition: 'width 0.18s ease, height 0.18s ease, border-radius 0.18s ease',
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            height: 60,
            borderBottom: `1px solid ${themeColors.neutral[100]}`,
            flexShrink: 0,
            backgroundColor: 'white',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {nodeIcon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  backgroundColor: nodeColor?.softBg || themeColors.neutral[100],
                  color: nodeColor?.text || themeColors.neutral[600],
                  flexShrink: 0,
                }}
              >
                {nodeIcon}
              </Box>
            )}
            <Box>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.9375rem',
                  color: themeColors.neutral[900],
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Typography>
              {nodeLabel && (
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: themeColors.neutral[400],
                    mt: 0.25,
                    lineHeight: 1.2,
                  }}
                >
                  {nodeLabel}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} placement="bottom">
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={() => setIsFullscreen((f) => !f)}
                sx={{ borderRadius: '8px' }}
              >
                {isFullscreen
                  ? <FullscreenExitIcon sx={{ fontSize: 18 }} />
                  : <FullscreenIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <IconButton size="sm" variant="plain" color="neutral" onClick={onClose} sx={{ borderRadius: '8px' }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* ── Variable chips ── */}
        {availableVariables.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.875,
              px: 3,
              py: 1.25,
              borderBottom: `1px solid ${themeColors.neutral[100]}`,
              backgroundColor: themeColors.neutral[50],
              flexShrink: 0,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            <Typography
              sx={{
                fontSize: '0.625rem',
                fontWeight: 700,
                color: themeColors.neutral[400],
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                flexShrink: 0,
                mr: 0.5,
              }}
            >
              Variables
            </Typography>
            {availableVariables.map((variable) => (
              <Box
                key={variable}
                component="button"
                onClick={() => insertVariable(variable)}
                title={`Click to insert {{${variable}}}`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '5px',
                  border: '1px solid rgba(59,77,214,0.2)',
                  backgroundColor: 'rgba(59,77,214,0.05)',
                  color: themeColors.primary[700],
                  fontFamily: EDITOR.fontFamily,
                  fontSize: '0.6875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  lineHeight: 1.5,
                  transition: 'all 0.12s ease',
                  outline: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(59,77,214,0.1)',
                    borderColor: 'rgba(59,77,214,0.35)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 6px rgba(59,77,214,0.12)',
                  },
                  '&:active': {
                    transform: 'none',
                    backgroundColor: 'rgba(59,77,214,0.15)',
                  },
                }}
              >
                {`{{${variable}}}`}
              </Box>
            ))}
          </Box>
        )}

        {/* ── Editor area ── */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'white',
          }}
        >
          {/* Display layer — renders highlighted HTML, scrolled via JS */}
          <Box
            ref={displayRef}
            aria-hidden="true"
            dangerouslySetInnerHTML={{ __html: displayHtml }}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              padding: EDITOR.padding,
              fontFamily: EDITOR.fontFamily,
              fontSize: EDITOR.fontSize,
              lineHeight: EDITOR.lineHeight,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowY: 'hidden', // scrolled programmatically to match textarea
              overflowX: 'hidden',
              color: themeColors.neutral[800],
              userSelect: 'none',
              pointerEvents: 'none',
              boxSizing: 'border-box',
              m: 0,
              zIndex: 0,
            }}
          />

          {/* Edit layer — transparent textarea; caret and selection stay visible */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onScroll={syncScroll}
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            data-gramm="false"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              padding: EDITOR.padding,
              fontFamily: EDITOR.fontFamily,
              fontSize: EDITOR.fontSize,
              lineHeight: EDITOR.lineHeight,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowY: 'auto',
              overflowX: 'hidden',
              color: 'transparent',
              caretColor: themeColors.neutral[900],
              background: 'transparent',
              border: 'none',
              outline: 'none',
              resize: 'none',
              boxSizing: 'border-box',
              margin: 0,
              zIndex: 1,
            }}
          />
        </Box>

        {/* ── Footer ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            height: 52,
            borderTop: `1px solid ${themeColors.neutral[100]}`,
            backgroundColor: themeColors.neutral[50],
            flexShrink: 0,
          }}
        >
          {/* Stats */}
          <Typography
            sx={{
              fontSize: '0.6875rem',
              color: savedFlash ? '#16a34a' : themeColors.neutral[400],
              fontFamily: EDITOR.fontFamily,
              transition: 'color 0.15s ease',
            }}
          >
            {savedFlash ? 'Saved ✓' : `${charCount.toLocaleString()} chars · ${lineCount} ${lineCount === 1 ? 'line' : 'lines'}`}
          </Typography>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Tooltip title={copySuccess ? 'Copied!' : 'Copy'} placement="top">
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={handleCopy}
                sx={{
                  borderRadius: '7px',
                  color: copySuccess ? '#16a34a' : themeColors.neutral[400],
                  '&:hover': { backgroundColor: themeColors.neutral[100] },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>

            <Button
              variant="plain"
              color="neutral"
              size="sm"
              onClick={onClose}
              sx={{
                borderRadius: '7px',
                fontWeight: 500,
                color: themeColors.neutral[600],
                '&:hover': { backgroundColor: themeColors.neutral[100] },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="solid"
              size="sm"
              onClick={handleSave}
              sx={{
                borderRadius: '7px',
                fontWeight: 600,
                fontSize: '0.8125rem',
                backgroundColor: themeColors.primary[500],
                color: 'white',
                '&:hover': { backgroundColor: themeColors.primary[600] },
                px: 2,
                height: 34,
              }}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
}
